<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

import { listDefects, raiseDefect } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const open = (id: string): void => {
  void router.push({
    name: "defect-detail",
    params: { projectId: selectedProjectId.value, defectId: id },
  });
};

const filters = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "closed", label: "Closed" },
];
const statusFilter = ref<string>("");
const snagsOnly = ref(false);

const { data, isPending, isError, error } = useQuery({
  queryKey: ["defects", selectedProjectId, statusFilter, snagsOnly],
  queryFn: () =>
    listDefects(
      selectedProjectId.value,
      statusFilter.value || undefined,
      snagsOnly.value,
    ),
});

const riskClass = (risk: string | null): string => {
  if (risk === "high") return "bg-semantic-danger/10 text-semantic-danger";
  if (risk === "medium") return "bg-semantic-warning/10 text-semantic-warning";
  return "bg-neutral-100 text-neutral-500";
};

// Where a register row came from (display only).
const sourceLabel = (d: { source: string; diary_id: string | null }): string =>
  d.source === "check_sheet" ? "ITP" : d.diary_id ? "Site diary" : "Office";

// ---- Office fallback: raise a defect from the web ----
const CATEGORIES = ["safety", "quality", "asset", "access", "environmental", "other"];
const RISKS = ["low", "medium", "high"];
const showRaise = ref(false);
const form = ref({ description: "", category: "safety", risk: "medium", location: "" });
const raiseBusy = ref(false);
const raiseError = ref<string | null>(null);
const canRaise = computed(() => form.value.description.trim().length > 0);

async function raise(): Promise<void> {
  if (!canRaise.value) return;
  raiseBusy.value = true;
  raiseError.value = null;
  try {
    const id = await raiseDefect({
      projectId: selectedProjectId.value as string,
      description: form.value.description.trim(),
      category: form.value.category,
      riskRating: form.value.risk,
      locationRef: form.value.location.trim() || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["defects"] });
    void router.push({
      name: "defect-detail",
      params: { projectId: selectedProjectId.value, defectId: id },
    });
  } catch (e) {
    raiseError.value = e instanceof Error ? e.message : String(e);
  } finally {
    raiseBusy.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <div class="flex items-center justify-between mb-4 gap-4">
      <h1 class="text-xl font-semibold">
        Defect register
      </h1>
      <div class="flex items-center gap-3">
        <div class="flex gap-1">
          <button
            v-for="f in filters"
            :key="f.value"
            type="button"
            class="text-sm rounded-md px-3 py-1"
            :class="
              statusFilter === f.value
                ? 'bg-brand-primary text-brand-primary-on'
                : 'bg-neutral-100 text-neutral-500 hover:text-neutral-900'
            "
            @click="statusFilter = f.value"
          >
            {{ f.label }}
          </button>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-md px-3 py-1 text-sm font-medium"
          :class="snagsOnly
            ? 'bg-semantic-warning/15 text-semantic-warning'
            : 'bg-neutral-100 text-neutral-500 hover:text-neutral-900'"
          @click="snagsOnly = !snagsOnly"
        >
          Snags only
        </button>
        <button
          type="button"
          class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          @click="showRaise = !showRaise"
        >
          {{ showRaise ? "Cancel" : "+ Raise defect" }}
        </button>
      </div>
    </div>

    <!-- Office fallback: raise a defect from the web -->
    <div
      v-if="showRaise"
      class="mb-4 rounded-xl border border-neutral-200 bg-neutral-0 p-4"
    >
      <p class="mb-3 text-sm text-neutral-500">
        Office fallback — defects are normally raised on site (from the diary or
        an ITP). Set priority, target date and owner after creating, on the
        defect's page.
      </p>
      <div class="grid gap-3">
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Description</span>
          <textarea
            v-model="form.description"
            rows="2"
            placeholder="What's the fault / non-conformance?"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </label>
        <div class="grid gap-3 sm:grid-cols-3">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Category</span>
            <select
              v-model="form.category"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm capitalize"
            >
              <option
                v-for="c in CATEGORIES"
                :key="c"
                :value="c"
              >
                {{ c }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Risk</span>
            <select
              v-model="form.risk"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm capitalize"
            >
              <option
                v-for="r in RISKS"
                :key="r"
                :value="r"
              >
                {{ r }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Location</span>
            <input
              v-model="form.location"
              type="text"
              placeholder="e.g. B2585"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
        </div>
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="!canRaise || raiseBusy"
            @click="raise"
          >
            {{ raiseBusy ? "Raising…" : "Raise defect" }}
          </button>
          <span
            v-if="raiseError"
            class="text-sm text-semantic-danger"
          >{{ raiseError }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="text-neutral-500"
    >
      No defects in this view.
    </div>
    <div
      v-else-if="data"
      class="rounded-xl bg-neutral-0 shadow overflow-hidden"
    >
      <table class="w-full text-sm">
        <thead class="bg-neutral-50 text-neutral-500 text-left">
          <tr>
            <th class="px-4 py-2 font-medium">
              Raised
            </th>
            <th class="px-4 py-2 font-medium">
              Description
            </th>
            <th class="px-4 py-2 font-medium">
              Category
            </th>
            <th class="px-4 py-2 font-medium">
              Risk
            </th>
            <th class="px-4 py-2 font-medium">
              Assigned
            </th>
            <th class="px-4 py-2 font-medium">
              Due
            </th>
            <th class="px-4 py-2 font-medium">
              Source
            </th>
            <th class="px-4 py-2 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="d in data"
            :key="d.id"
            class="border-t border-neutral-100 align-top cursor-pointer hover:bg-neutral-50"
            @click="open(d.id)"
          >
            <td class="px-4 py-2 whitespace-nowrap">
              {{ d.raised_at.slice(0, 10) }}
            </td>
            <td class="px-4 py-2">
              {{ d.description }}
              <span
                v-if="d.is_snag"
                class="ml-1 rounded bg-semantic-warning/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-semantic-warning"
              >Snag</span>
            </td>
            <td class="px-4 py-2 capitalize">
              {{ d.category }}
            </td>
            <td class="px-4 py-2">
              <span
                class="rounded-full px-2 py-0.5 text-xs capitalize"
                :class="riskClass(d.risk_rating)"
              >
                {{ d.risk_rating ?? "—" }}
              </span>
            </td>
            <td class="px-4 py-2 whitespace-nowrap">
              {{ d.assigned_to_name ?? "—" }}
            </td>
            <td
              class="px-4 py-2 whitespace-nowrap"
              :class="d.target_date && d.status !== 'closed' && d.target_date < new Date().toISOString().slice(0, 10) ? 'text-semantic-danger font-medium' : 'text-neutral-500'"
            >
              {{ d.target_date ?? "—" }}
            </td>
            <td class="px-4 py-2">
              <RouterLink
                v-if="d.source === 'check_sheet' && d.check_sheet_id"
                :to="{
                  name: 'check-sheet-detail',
                  params: { projectId: selectedProjectId, id: d.check_sheet_id },
                }"
                class="text-brand-primary hover:underline"
                @click.stop
              >
                ITP check sheet
              </RouterLink>
              <span
                v-else
                class="text-neutral-400"
              >{{ sourceLabel(d) }}</span>
            </td>
            <td class="px-4 py-2 capitalize">
              {{ d.status.replace("_", " ") }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </PageLayout>
</template>
